/**
 * TaskCard — a task in a list: the Tasks section of a record slideout, and a
 * proposed task in an action card.
 *
 * A task is not a note. It is assigned rather than authored and rides a stage
 * board, so it leads with what you act on: the title, then the description,
 * then Assignee / Status / Due date as labelled rows. Every value edits in place
 * on click — the title and description through their own inline editors, the
 * rows through TaskFieldRows (the canonical InlineEditableFieldRow) — so there
 * is no edit button and no form to open.
 *
 * Where each value is saved is the caller's business: a saved task writes CRM
 * events, a proposed task writes the proposal's actions. A withheld handler
 * leaves that value read-only.
 */

import { Trash01 } from '@untitledui/icons';
import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

import type { Note } from '../memo/notes.types';
import { TaskDescriptionEditor } from './TaskDescriptionEditor';
import { CARD_TASK_FIELD_ORDER } from './taskFieldOrder';
import { TaskFieldRows } from './TaskFieldRows';
import { TaskTitleField } from './TaskTitleField';
// TaskDescriptionEditor always takes a save handler; it is only reachable when
// `canEdit` is true, which is exactly when a real one is supplied. Reuse the
// shared no-op so the task editors keep one fallback implementation.
import { NOOP_SAVE } from './useInlineFieldEdit';

export interface TaskCardProps {
  /** The task's title, description and due date (memo record shape). */
  task: Note;
  orgId: number;
  /**
   * Stage and assignee are passed explicitly rather than read off `task`: a
   * proposed task carries them as sibling association actions, and may fall
   * back to the org's default stage.
   */
  stageEntityId: string | null;
  ownerEntityId: string | null;
  dueDateSchema: PropertyTypeSchema | undefined;
  assigneeAssociationType: AssociationType | undefined;
  onSaveTitle?: (title: string) => Promise<void>;
  onSaveDescription?: (content: string) => Promise<void>;
  onSaveDue?: (newDate: string | null) => Promise<void>;
  onSaveAssignee?: (targetIds: string[]) => Promise<void>;
  onStageChange?: (
    taskEntityId: string,
    newStageEntityId: string | null
  ) => Promise<void>;
  /** Supplied → a delete button with a confirmation step. Should reject on failure. */
  onDelete?: (taskEntityId: string) => Promise<unknown>;
  isDeleting?: boolean;
  deleteError?: Error | null;
  /**
   * Whether the assignee may be cleared. Defaults to true. A proposed task
   * carries its owner as an association-add action with no removal path, so its
   * card passes false — see TaskFieldRows.allowAssigneeClear.
   */
  allowAssigneeClear?: boolean;
}

export function TaskCard({
  task,
  orgId,
  stageEntityId,
  ownerEntityId,
  dueDateSchema,
  assigneeAssociationType,
  onSaveTitle,
  onSaveDescription,
  onSaveDue,
  onSaveAssignee,
  onStageChange,
  onDelete,
  isDeleting = false,
  deleteError = null,
  allowAssigneeClear = true,
}: TaskCardProps) {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const handleDeleteClick = useCallback(() => {
    setIsConfirmingDelete(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setIsConfirmingDelete(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!onDelete) {
      return;
    }
    try {
      await onDelete(task.entity_id);
      setIsConfirmingDelete(false);
    } catch {
      // The caller surfaces `deleteError`; keep the confirmation open so it
      // renders and the user can retry.
    }
  }, [onDelete, task.entity_id]);

  if (isConfirmingDelete) {
    return (
      <div className='rounded-xl border border-utility-error-200 bg-primary p-4'>
        <p className='mb-3 text-sm text-primary'>
          Are you sure you want to delete this task? This action cannot be
          undone.
        </p>
        <div className='flex justify-end gap-2'>
          <Button
            color='secondary'
            size='sm'
            onClick={handleDeleteCancel}
            isDisabled={isDeleting}
            disabledTooltip='Deleting the task…'
          >
            Cancel
          </Button>
          <Button
            color='primary-destructive'
            size='sm'
            onClick={handleDeleteConfirm}
            isDisabled={isDeleting}
            disabledTooltip='Deleting the task…'
            isLoading={isDeleting}
          >
            Delete
          </Button>
        </div>
        {deleteError && (
          <p className='mt-2 text-xs text-utility-error-800'>
            We couldn&apos;t delete the task. Try again.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className='rounded-xl border border-primary bg-primary p-3'>
      <div className='flex items-start gap-2'>
        <div className='min-w-0 flex-1'>
          <TaskTitleField title={task.title} onSave={onSaveTitle} />
        </div>
        {onDelete && (
          <Button
            type='button'
            iconLeading={Trash01}
            color='tertiary'
            size='sm'
            className='!size-6 !p-1 [&_[data-icon]]:!size-3.5 hover:!text-utility-error-600'
            onClick={handleDeleteClick}
            aria-label='Delete task'
            title='Delete task'
          />
        )}
      </div>
      <div className='mt-1'>
        <TaskDescriptionEditor
          content={task.content}
          orgId={orgId}
          canEdit={onSaveDescription !== undefined}
          onSave={onSaveDescription ?? NOOP_SAVE}
        />
      </div>
      {/* Same row rhythm as the slideout Overview (12px between rows, 6px of
          cell bleed), so the card and the slideout read as one layout. */}
      <div className='mt-2 space-y-3 py-1.5'>
        <TaskFieldRows
          order={CARD_TASK_FIELD_ORDER}
          orgId={orgId}
          taskEntityId={task.entity_id}
          stageEntityId={stageEntityId}
          ownerEntityId={ownerEntityId}
          date={task.date}
          datetime={task.datetime}
          dueDateSchema={dueDateSchema}
          assigneeAssociationType={assigneeAssociationType}
          onStageChange={onStageChange}
          onSaveDue={onSaveDue}
          onSaveAssignee={onSaveAssignee}
          allowAssigneeClear={allowAssigneeClear}
        />
      </div>
    </div>
  );
}
